import React from 'react';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import { Editor } from 'primereact/editor';
import MyBriefsCmsPublications from './MyBriefsCmsPublications';
import { useIsAdmin } from '../../hooks/useFrontegg';
import { confineQuillTooltip } from '../../utils/quillLinkTooltip';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'quill/dist/quill.snow.css';
import {
  formatStatusLabel,
  normalizeBriefBodyEditorOutput,
  normalizeBriefBodyForEditor,
  resolveSavedBriefResult,
} from './myBriefsUtils';

// 'bullet' is a VALUE of the 'list' format, not a format — registering it crashes Quill 2
const BODY_EDITOR_FORMATS = ['bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'link'];

const BODY_EDITOR_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
};

const BODY_EDITOR_HEADER = (
  <>
    <span className="ql-formats">
      <button type="button" className="ql-bold" aria-label="Bold" />
      <button type="button" className="ql-italic" aria-label="Italic" />
      <button type="button" className="ql-underline" aria-label="Underline" />
      <button type="button" className="ql-strike" aria-label="Strike" />
    </span>
    <span className="ql-formats">
      <button type="button" className="ql-list" value="ordered" aria-label="Ordered list" />
      <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" />
      <button type="button" className="ql-blockquote" aria-label="Blockquote" />
    </span>
    <span className="ql-formats">
      <button type="button" className="ql-link" aria-label="Link" />
      <button type="button" className="ql-clean" aria-label="Clear formatting" />
    </span>
  </>
);

function SpinnerRow({ label }) {
  return (
    <div className="my-briefs-expanded-spinner-row">
      <span className="my-briefs-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function renderBriefContent(content) {
  if (!content) return null;
  return (
    <div
      className="my-briefs-richtext-rendered"
      dir="auto"
      dangerouslySetInnerHTML={{ __html: normalizeBriefBodyForEditor(content) }}
    />
  );
}

function ActionButton({ children, onClick, disabled, variant = 'secondary', icon = null }) {
  return (
    <button
      type="button"
      className={`my-briefs-action-button ${variant}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon ? <span className="my-briefs-action-button-icon" aria-hidden="true">{icon}</span> : null}
      {children}
    </button>
  );
}

function renderPreservedReadyContent(detail, preferredVariant = null) {
  const resolvedResult = resolveSavedBriefResult(detail, preferredVariant);
  const header = resolvedResult.content?.header || '';
  const subheader = resolvedResult.content?.subheader || '';
  const brief = resolvedResult.content?.brief || '';

  if (!header && !subheader && !brief) return null;

  return (
    <>
      {header ? <p className="my-briefs-preserved-field"><strong>Header:</strong> {header}</p> : null}
      {subheader ? <p className="my-briefs-preserved-field"><strong>Subheader:</strong> {subheader}</p> : null}
      {brief ? renderBriefContent(brief) : null}
    </>
  );
}

function MyBriefsExpandedContent({
  item,
  detail,
  canManage,
  isLoading,
  editDraft,
  isDirty,
  isSaving,
  saveStatus = null,
  onEditFieldChange,
  onSave,
  onCopy,
  onPublishCms,
  onRegenerate,
  onRetry,
  onDelete,
  onRestore,
  onPermanentDelete,
  onOpenTranslate,
  isNewsletter = false,
  isAssembled = false,
  onSendToClient,
  comparisonView = null,
  onComparisonViewChange = null,
}) {
  const isAdmin = useIsAdmin();
  const newsletterMeta = detail?.result?.newsletterMeta || null;
  const newsletterOrder = newsletterMeta?.order || [];
  const newsletterDropped = newsletterMeta?.droppedStories || [];

  if (isLoading) {
    return <SpinnerRow label="Loading draft details" />;
  }

  if (!detail) {
    return <div className="my-briefs-expanded-message">Draft details are unavailable.</div>;
  }

  if (detail.status === 'in_progress') {
    return <SpinnerRow label="Draft is in progress" />;
  }

  if (detail.status === 'regenerating') {
    return <SpinnerRow label="Draft is regenerating" />;
  }

  if (detail.status === 'deleted') {
    return (
      <div className="my-briefs-expanded-content-body">
        <div className="my-briefs-expanded-summary-row">
          <span className="my-briefs-badge">{formatStatusLabel(detail.status)}</span>
        </div>
        {canManage ? (
          <div className="my-briefs-action-row my-briefs-action-row-centered">
            <ActionButton onClick={onRestore}>Restore</ActionButton>
            <ActionButton onClick={onPermanentDelete} variant="danger">Permanently delete</ActionButton>
          </div>
        ) : (
          <div className="my-briefs-readonly-note">Viewing another user&apos;s draft. Lifecycle actions are disabled.</div>
        )}
      </div>
    );
  }

  const resolvedResult = resolveSavedBriefResult(detail, comparisonView);
  const hasComparison = resolvedResult.hasComparison;
  const resultContent = resolvedResult.content || {};

  if (detail.status === 'failed') {
    const hasPreservedResult = Boolean(resultContent?.brief || resultContent?.header || resultContent?.subheader);
    const canCopy = Boolean(detail.partialContent || hasPreservedResult);
    return (
      <div className="my-briefs-expanded-content-body">
        <div className="my-briefs-expanded-summary-row">
          <span className="my-briefs-badge">{formatStatusLabel(detail.status)}</span>
        </div>
        {detail.partialContent ? (
          <>
            <div className="my-briefs-expanded-message status-failed">Failed. Showing partial content.</div>
            {renderBriefContent(detail.partialContent)}
          </>
        ) : hasPreservedResult ? (
          <>
            <div className="my-briefs-expanded-message status-failed">
              {detail?.error?.message || 'Regeneration failed. Showing the last saved content.'}
            </div>
            {renderPreservedReadyContent(detail, comparisonView)}
          </>
        ) : (
          <div className="my-briefs-expanded-message status-failed">
            {detail?.error?.message || 'Draft generation failed and no content is available.'}
          </div>
        )}
        {!canManage ? <div className="my-briefs-readonly-note">Viewing another user&apos;s draft. Retry and archive are disabled.</div> : null}
        <div className="my-briefs-action-row my-briefs-action-row-centered">
          {canManage ? <ActionButton onClick={onDelete} variant="danger" icon={<ArchiveOutlinedIcon style={{ fontSize: 16 }} />}>Archive</ActionButton> : null}
          {canManage ? <ActionButton onClick={onRetry} icon={<AutorenewOutlinedIcon style={{ fontSize: 16 }} />}>Retry</ActionButton> : null}
          {canCopy ? <ActionButton onClick={onCopy} icon={<ContentCopyOutlinedIcon style={{ fontSize: 16 }} />}>Copy</ActionButton> : null}
        </div>
      </div>
    );
  }

  const header = editDraft?.header || '';
  const subheader = editDraft?.subheader || '';
  const brief = editDraft?.brief || '';
  const titleOptions = Array.isArray(resultContent?.titles) ? resultContent.titles.filter(Boolean) : [];
  const selectedTitle = header || titleOptions[0] || '';

  const modelUsed = detail?.model || detail?.requestPayload?.model || null;
  const translationModelUsed = detail?.translationModel || detail?.requestPayload?.translationModel || null;
  const promptTypeUsed = detail?.promptType || detail?.requestPayload?.promptType || null;
  const showTranslationBadge = isAdmin && translationModelUsed && (detail?.language && detail.language !== 'English');
  const showPromptTypeBadge = isAdmin && promptTypeUsed && promptTypeUsed !== 'DEFAULT_BRIEF';
  const showModelBadge = isAdmin && !!modelUsed;
  const showPipeline = isAdmin && Array.isArray(detail?.modelPipeline) && detail.modelPipeline.length > 0;

  return (
    <div className="my-briefs-expanded-content-body" data-testid="my-briefs-expanded-content-body">
      {!canManage ? <div className="my-briefs-readonly-note">Viewing another user&apos;s draft. Editing and lifecycle actions are disabled.</div> : null}
      {(showModelBadge || showTranslationBadge || showPromptTypeBadge) ? (
        <div className="my-briefs-expanded-summary-row" data-testid="my-briefs-model-meta">
          {showModelBadge ? (
            <span className="my-briefs-badge" title="Generation model used for this draft">
              Generated with {modelUsed}
            </span>
          ) : null}
          {showTranslationBadge ? (
            <span className="my-briefs-badge" title="Translation backend used for this draft">
              Translation: {translationModelUsed}
            </span>
          ) : null}
          {showPromptTypeBadge ? (
            <span className="my-briefs-badge" title="Prompt variant used for this draft">
              Prompt: {promptTypeUsed}
            </span>
          ) : null}
        </div>
      ) : null}
      {showPipeline ? (
        <details className="my-briefs-model-pipeline" data-testid="my-briefs-model-pipeline">
          <summary>Model pipeline ({detail.modelPipeline.length} step{detail.modelPipeline.length === 1 ? '' : 's'})</summary>
          <table className="my-briefs-model-pipeline-table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {detail.modelPipeline.map((step, index) => (
                <tr key={`pipeline-${index}`}>
                  <td>{step.label || step.phase}</td>
                  <td>{step.api || '—'}</td>
                  <td>{step.model || '—'}</td>
                  <td>{typeof step.ms === 'number' ? `${(step.ms / 1000).toFixed(1)}s` : '—'}</td>
                </tr>
              ))}
              <tr className="my-briefs-model-pipeline-total">
                <td colSpan="3">Total AI time</td>
                <td>{`${(detail.modelPipeline.reduce((sum, s) => sum + (Number(s.ms) || 0), 0) / 1000).toFixed(1)}s`}</td>
              </tr>
            </tbody>
          </table>
        </details>
      ) : null}
      {/* The source notice, OUTSIDE the editable article text so it cannot be
          copied into the published copy or edited away.
          BriefComponent already rendered it, but a restricted draft is queued
          and opened HERE, in My Drafts — so for every draft that actually
          carries the exactly-two-publishers warning, the only surface a person
          sees never showed it. The backend decides the wording; both places
          only display it. */}
      {detail?.result?.sourceNotice ? (
        <div
          role="note"
          className="my-briefs-source-notice"
          data-testid={`my-briefs-source-notice-${item._id}`}
          style={{
            margin: '0 0 12px',
            padding: '10px 12px',
            borderRadius: '8px',
            background: 'rgba(255,152,0,0.10)',
            border: '1px solid rgba(255,152,0,0.35)',
            color: '#ad6300',
            fontSize: '13px',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {detail.result.sourceNotice}
          {detail.result.restrictedScope ? ` Draft sources: ${detail.result.restrictedScope}.` : ''}
        </div>
      ) : null}
      <div className="my-briefs-editor-surface">
        {hasComparison ? (
          <div className="my-briefs-comparison-toggle" role="tablist" aria-label="Draft comparison view">
            {resolvedResult.availableVariants.includes('claimsBased') ? (
              <button
                type="button"
                role="tab"
                aria-selected={resolvedResult.variant === 'claimsBased'}
                className={`my-briefs-comparison-toggle-button${resolvedResult.variant === 'claimsBased' ? ' selected' : ''}`}
                onClick={() => onComparisonViewChange?.('claimsBased')}
              >
                Claims-based
              </button>
            ) : null}
            {resolvedResult.availableVariants.includes('claimFree') ? (
              <button
                type="button"
                role="tab"
                aria-selected={resolvedResult.variant === 'claimFree'}
                className={`my-briefs-comparison-toggle-button${resolvedResult.variant === 'claimFree' ? ' selected' : ''}`}
                onClick={() => onComparisonViewChange?.('claimFree')}
              >
                Claim-free
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="my-briefs-editor-section">
          <div className="my-briefs-editor-section-label">
            <span className="my-briefs-editor-accent" aria-hidden="true" />
            <span className="my-briefs-editor-section-text">Headline</span>
          </div>
          {titleOptions.length > 0 ? (
            <div className="my-briefs-headline-options" data-testid="my-briefs-headline-options">
              {titleOptions.map((title, index) => {
                const isSelected = selectedTitle === title;
                return (
                  <button
                    key={`${item._id}-title-${index}`}
                    type="button"
                    className={`my-briefs-headline-option${isSelected ? ' selected' : ''}`}
                    onClick={() => onEditFieldChange('header', title)}
                    disabled={!canManage || isSaving}
                  >
                    <span className={`my-briefs-headline-option-check${isSelected ? ' selected' : ''}`} aria-hidden="true" />
                    <span className="my-briefs-headline-option-text">{title}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          <label className="my-briefs-edit-label" htmlFor={`brief-header-${item._id}`}>
            Header
          </label>
          <input
            id={`brief-header-${item._id}`}
            type="text"
            dir="auto"
            value={header}
            className="my-briefs-edit-input"
            onChange={(event) => onEditFieldChange('header', event.target.value)}
            disabled={!canManage || isSaving}
          />
        </div>

        <div className="my-briefs-editor-section">
          <div className="my-briefs-editor-section-label">
            <span className="my-briefs-editor-accent" aria-hidden="true" />
            <span className="my-briefs-editor-section-text">Subheader</span>
          </div>
          <input
            id={`brief-subheader-${item._id}`}
            type="text"
            dir="auto"
            value={subheader}
            aria-label="Subheader"
            className="my-briefs-edit-input"
            onChange={(event) => onEditFieldChange('subheader', event.target.value)}
            disabled={!canManage || isSaving}
          />
        </div>

        {/* §10 / answer G6 — every newsletter item must be traceable back to
            its originating bucket story. The anchors are generated server-side
            when the body is assembled, so this list is reliable rather than a
            guess at which paragraph came from where. */}
        {isNewsletter && newsletterOrder.length > 0 ? (
          <div className="my-briefs-newsletter-sources">
            <div className="my-briefs-editor-section-label">
              <span className="my-briefs-editor-accent" aria-hidden="true" />
              <span className="my-briefs-editor-section-text">
                Stories in this newsletter ({newsletterOrder.length})
              </span>
            </div>
            <ol className="my-briefs-newsletter-source-list">
              {newsletterOrder.map((entry) => (
                <li key={entry.storyId}>
                  {entry.storySlug ? (
                    <a
                      href={`/story/${entry.storySlug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {entry.storyTitle || 'Untitled story'}
                    </a>
                  ) : (
                    <span>{entry.storyTitle || 'Untitled story'}</span>
                  )}
                </li>
              ))}
            </ol>
            {newsletterDropped.length > 0 ? (
              <p className="my-briefs-newsletter-dropped">
                {newsletterDropped.length} selected{' '}
                {newsletterDropped.length === 1 ? 'story was' : 'stories were'}{' '}
                left out because a draft could not be generated:{' '}
                {newsletterDropped.map((d) => d.storyTitle || 'Untitled story').join(', ')}.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="my-briefs-editor-section">
          <div className="my-briefs-editor-section-label">
            <span className="my-briefs-editor-accent" aria-hidden="true" />
            <span className="my-briefs-editor-section-text">Body</span>
          </div>
          <Editor
            onLoad={confineQuillTooltip}
            id={`brief-body-${item._id}`}
            value={brief}
            showHeader={canManage}
            headerTemplate={BODY_EDITOR_HEADER}
            modules={BODY_EDITOR_MODULES}
            formats={BODY_EDITOR_FORMATS}
            placeholder="Draft"
            className="my-briefs-richtext-editor my-briefs-richtext-editor-body"
            readOnly={!canManage || isSaving}
            pt={{
              root: { 'data-testid': `my-briefs-body-editor-${item._id}` },
              content: { 'aria-label': 'Draft', dir: 'auto' },
            }}
            onTextChange={(event) => onEditFieldChange('brief', normalizeBriefBodyEditorOutput(event.htmlValue))}
          />
        </div>
      </div>

      <MyBriefsCmsPublications
        publications={detail?.cmsPublications || item?.cmsPublications}
        briefId={item._id}
        defaultOpen={false}
      />

      {canManage && saveStatus ? (
        <div
          className={`my-briefs-save-status my-briefs-save-status-${saveStatus}`}
          data-testid={`my-brief-save-status-${item._id}`}
          role="status"
          aria-live="polite"
        >
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && 'Saved'}
          {saveStatus === 'unsaved' && 'Unsaved changes — autosaving…'}
          {saveStatus === 'failed' && 'Autosave failed. Click Save changes to retry — leaving the page will lose your edits.'}
        </div>
      ) : null}

      <div className="my-briefs-action-row my-briefs-action-row-centered">
        {isDirty && canManage ? (
          <ActionButton onClick={onSave} disabled={isSaving} variant="primary">
            {isSaving ? 'Saving...' : 'Save changes'}
          </ActionButton>
        ) : null}
        {canManage ? <ActionButton onClick={onDelete} disabled={isSaving} variant="danger" icon={<ArchiveOutlinedIcon style={{ fontSize: 16 }} />}>Archive</ActionButton> : null}
        {/* An assembled artifact — a newsletter (answers G3/G5) or a media
            analysis (§C3) — has no single story to regenerate from, cannot be
            re-translated in place, and is not a news item a CMS would accept.
            Clipboard and Send to client are its exports. Before this gate
            covered media analyses too, all three buttons rendered on one and
            Regenerate would have re-run brief generation against the empty
            claim/article arrays a media analysis carries. */}
        {canManage && !isAssembled ? <ActionButton onClick={onRegenerate} disabled={isSaving} icon={<AutorenewOutlinedIcon style={{ fontSize: 16 }} />}>Regenerate</ActionButton> : null}
        <ActionButton onClick={onCopy} disabled={isSaving} icon={<ContentCopyOutlinedIcon style={{ fontSize: 16 }} />}>Copy</ActionButton>
        {canManage && !isAssembled && onOpenTranslate ? (
          <ActionButton
            onClick={onOpenTranslate}
            disabled={isSaving}
            icon={<TranslateOutlinedIcon style={{ fontSize: 16 }} />}
          >
            Translate
          </ActionButton>
        ) : null}
        {canManage && !isAssembled && onPublishCms ? <ActionButton onClick={onPublishCms} disabled={isSaving} icon={<PublishOutlinedIcon style={{ fontSize: 16 }} />}>Publish to CMS</ActionButton> : null}
        {/* PRD §C3 — "Add Send to client to the main Draft editor near Send to
            CMS." It sits beside Publish to CMS because they are the same kind of
            action from an author's point of view: this draft leaves the
            building. They differ in where it goes and who reads it.

            Unlike Publish to CMS this is NOT newsletter-excluded: §C3 says the
            same sharing foundation should serve stories, newsletters and Media
            Analysis, and a newsletter is precisely the sort of thing a client is
            sent. */}
        {canManage && onSendToClient ? (
          <ActionButton
            onClick={onSendToClient}
            disabled={isSaving}
            icon={<SendOutlinedIcon style={{ fontSize: 16 }} />}
          >
            Send to client
          </ActionButton>
        ) : null}
      </div>
    </div>
  );
}

export default MyBriefsExpandedContent;
